import {
  httpGet,
  httpGetForBinary,
  httpPostJson,
  httpPostJsonForBinary,
  httpUploadFile,
  httpUploadBytes,
  HttpResponse,
} from './httpClient';
import {
  BackupResponse,
  ChatResponse,
  ClaimsResponse,
  DashboardResponse,
  HealthRecord,
  PairingBundle,
  RecordContent,
  RestoreResponse,
  SynthesizedAudio,
  TranscribeResponse,
} from './types';

// 512KB: comfortably under the size that broke a real device's HTTP
// plugin bridge (a ~5MB single call failed; ~3.6MB worked) — see
// uploadDocumentChunked's docstring.
const UPLOAD_CHUNK_SIZE = 512 * 1024;

export class DomiHealthApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'DomiHealthApiError';
  }
}

/** The per-user local passphrase (not the wallet's own password) was
 * wrong for an existing edge-node user database. */
export class IncorrectPassphraseError extends DomiHealthApiError {}

/** This user's session isn't unlocked on the edge node — call `unlock`
 * first. Distinct from `IncorrectPassphraseError` since it can also
 * happen simply because the edge node process restarted. */
export class LockedError extends DomiHealthApiError {}

function parseJsonOrThrow<T>(response: HttpResponse, context: string): T {
  if (response.status === 401) {
    throw new IncorrectPassphraseError(response.status, `${context}: incorrect passphrase`);
  }
  if (response.status === 423) {
    throw new LockedError(response.status, `${context}: user is locked — unlock first`);
  }
  if (response.status < 200 || response.status >= 300) {
    throw new DomiHealthApiError(
      response.status,
      `${context} failed (HTTP ${response.status}): ${response.data}`,
    );
  }
  try {
    return JSON.parse(response.data) as T;
  } catch (err) {
    throw new DomiHealthApiError(response.status, `${context}: response was not valid JSON`);
  }
}

/** Thin, typed wrapper over every route `service.py` exposes. One
 * instance per pairing bundle — construct a new one if the user re-pairs
 * with a different (or the same, updated) edge node. */
export default class DomiHealthApi {
  private bundle: PairingBundle;

  constructor(bundle: PairingBundle) {
    this.bundle = bundle;
  }

  async unlock(userId: string, passphrase: string): Promise<void> {
    const response = await httpPostJson(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/unlock`,
      { passphrase },
    );
    parseJsonOrThrow<{ status: string }>(response, 'unlock');
  }

  async lock(userId: string): Promise<void> {
    const response = await httpPostJson(this.bundle, `/users/${encodeURIComponent(userId)}/lock`, {});
    parseJsonOrThrow<{ status: string }>(response, 'lock');
  }

  /** One-time migration for an account still encrypted with an old,
   * user-typed passphrase (from before DomiHealthPage.tsx moved to
   * walletBridge.deriveUnlockPassphrase) -- switches the database over
   * to the given (derived) passphrase so future unlocks never need the
   * manual fallback again. Requires an already-unlocked session, same
   * as the edge node route itself. */
  async rekey(userId: string, newPassphrase: string): Promise<void> {
    const response = await httpPostJson(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/rekey`,
      { new_passphrase: newPassphrase },
    );
    parseJsonOrThrow<{ status: string }>(response, 'rekey');
  }

  async addMeasurement(
    userId: string,
    metricType: string,
    value: unknown,
    unit?: string,
    action: 'collect' | 'record' = 'collect',
  ): Promise<HealthRecord> {
    const response = await httpPostJson(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/measurements`,
      { metric_type: metricType, value, unit, action },
    );
    return parseJsonOrThrow<HealthRecord>(response, 'addMeasurement');
  }

  async listRecords(userId: string): Promise<HealthRecord[]> {
    const response = await httpGet(this.bundle, `/users/${encodeURIComponent(userId)}/records`);
    return parseJsonOrThrow<HealthRecord[]>(response, 'listRecords');
  }

  /** Fetches the raw bytes behind a "download"-action record (the
   * original uploaded photo/PDF) — `/records` itself never ships this,
   * only metadata, so viewing or saving a specific file is a separate
   * fetch. 404s when the record has no binary content (e.g. a plain
   * measurement, which was never a document to begin with). */
  async getRecordContent(userId: string, recordId: string): Promise<RecordContent> {
    const response = await httpGetForBinary(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/records/${encodeURIComponent(recordId)}/content`,
    );
    if (response.status === 404) {
      throw new DomiHealthApiError(response.status, 'getRecordContent: this record has no file content');
    }
    if (response.status < 200 || response.status >= 300 || response.data === null) {
      throw new DomiHealthApiError(response.status, `getRecordContent failed (HTTP ${response.status})`);
    }
    return { data: response.data, contentType: response.contentType };
  }

  /** Every record plus a summary of which ones have flagged lab values
   * (computed once at upload time on the edge node, not recomputed
   * here) — the data source for the dashboard's abnormal-items view. */
  async getDashboard(userId: string): Promise<DashboardResponse> {
    const response = await httpGet(this.bundle, `/users/${encodeURIComponent(userId)}/dashboard`);
    return parseJsonOrThrow<DashboardResponse>(response, 'getDashboard');
  }

  /** `fileUri` is a `file://` URI as returned by
   * `navigator.camera.getPicture({ destinationType: Camera.DestinationType.FILE_URI })`. */
  async uploadDocument(userId: string, metricType: string, fileUri: string): Promise<HealthRecord> {
    const response = await httpUploadFile(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/downloads`,
      { metric_type: metricType },
      'file',
      fileUri,
    );
    if (response.status === 413) {
      throw new DomiHealthApiError(response.status, 'uploadDocument: file is too large');
    }
    return parseJsonOrThrow<HealthRecord>(response, 'uploadDocument');
  }

  /** Same route as `uploadDocument`, for a file picked via
   * `filePicker.ts`'s `pickFileForUpload` — that plugin hands back raw
   * bytes rather than a native file:// path, so this goes through
   * `httpUploadBytes` (a hand-built multipart body) instead of
   * `httpUploadFile` (which needs a real path `uploadFile()` can read). */
  async uploadDocumentBytes(
    userId: string,
    metricType: string,
    data: Uint8Array,
    fileName: string,
    mediaType: string,
  ): Promise<HealthRecord> {
    const response = await httpUploadBytes(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/downloads`,
      { metric_type: metricType },
      'file',
      fileName,
      mediaType,
      data,
    );
    if (response.status === 413) {
      throw new DomiHealthApiError(response.status, 'uploadDocumentBytes: file is too large');
    }
    return parseJsonOrThrow<HealthRecord>(response, 'uploadDocumentBytes');
  }

  /** Like `uploadDocumentBytes`, but splits the file into small chunks
   * sent as separate requests instead of one call carrying the whole
   * thing. Needed for real device traffic (2026-09-13): a ~5MB file
   * came back as a local HTTP 413 from `uploadDocumentBytes` that never
   * even reached the edge node — the native HTTP plugin's JS-to-native
   * bridge choked marshalling that much binary data across it in one
   * call (a smaller ~3.6MB file had worked moments earlier through the
   * exact same code path). Splitting into small pieces, each its own
   * bridge call, sidesteps that regardless of total file size. */
  async uploadDocumentChunked(
    userId: string,
    metricType: string,
    data: Uint8Array,
    fileName: string,
    mediaType: string,
  ): Promise<HealthRecord> {
    const initResponse = await httpPostJson(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/uploads/init`,
      { metric_type: metricType, filename: fileName, content_type: mediaType },
    );
    const { upload_id: uploadId } = parseJsonOrThrow<{ upload_id: string }>(
      initResponse,
      'uploadDocumentChunked:init',
    );

    for (let offset = 0; offset < data.length; offset += UPLOAD_CHUNK_SIZE) {
      const chunk = data.subarray(offset, offset + UPLOAD_CHUNK_SIZE);
      const chunkResponse = await httpUploadBytes(
        this.bundle,
        `/users/${encodeURIComponent(userId)}/uploads/${uploadId}/chunk`,
        {},
        'file',
        fileName,
        'application/octet-stream',
        chunk,
      );
      if (chunkResponse.status === 413) {
        throw new DomiHealthApiError(chunkResponse.status, 'uploadDocumentChunked: file is too large');
      }
      if (chunkResponse.status < 200 || chunkResponse.status >= 300) {
        throw new DomiHealthApiError(
          chunkResponse.status,
          `uploadDocumentChunked: chunk upload failed (HTTP ${chunkResponse.status}): ${chunkResponse.data}`,
        );
      }
    }

    const completeResponse = await httpPostJson(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/uploads/${uploadId}/complete`,
      {},
    );
    return parseJsonOrThrow<HealthRecord>(completeResponse, 'uploadDocumentChunked:complete');
  }

  async buildClaims(userId: string, userWallet: string): Promise<ClaimsResponse> {
    const response = await httpPostJson(this.bundle, `/users/${encodeURIComponent(userId)}/claims`, {
      user_wallet: userWallet,
    });
    return parseJsonOrThrow<ClaimsResponse>(response, 'buildClaims');
  }

  async confirmClaim(userId: string, recordId: string, txSignature: string): Promise<void> {
    const response = await httpPostJson(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/claims/confirm`,
      { record_id: recordId, tx_signature: txSignature },
    );
    parseJsonOrThrow<{ status: string }>(response, 'confirmClaim');
  }

  async createBackup(
    userId: string,
    userWallet: string,
    walletSignatureHex: string,
    remotePinning: Array<{ endpoint: string; access_token: string }> = [],
  ): Promise<BackupResponse> {
    const response = await httpPostJson(this.bundle, `/users/${encodeURIComponent(userId)}/backup`, {
      user_wallet: userWallet,
      wallet_signature: walletSignatureHex,
      remote_pinning: remotePinning,
    });
    return parseJsonOrThrow<BackupResponse>(response, 'createBackup');
  }

  async restoreBackup(
    userId: string,
    userWallet: string,
    walletSignatureHex: string,
  ): Promise<RestoreResponse> {
    const response = await httpPostJson(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/backup/restore`,
      { user_wallet: userWallet, wallet_signature: walletSignatureHex },
    );
    if (response.status === 404) {
      throw new DomiHealthApiError(response.status, 'restoreBackup: this wallet has never backed up');
    }
    return parseJsonOrThrow<RestoreResponse>(response, 'restoreBackup');
  }

  async pinBackup(userId: string, userWallet: string): Promise<{ cid: string | null }> {
    const response = await httpPostJson(this.bundle, `/users/${encodeURIComponent(userId)}/backup/pin`, {
      user_wallet: userWallet,
    });
    return parseJsonOrThrow<{ cid: string | null }>(response, 'pinBackup');
  }

  /** One turn of Q&A with the wellness assistant — grounded in this
   * user's own records on the edge node side. Requires an unlocked
   * session (it reads those records) and a configured Ollama server on
   * the edge node (503 otherwise).
   *
   * `voice` controls reply length/style on the edge node side: pass
   * `true` only for the spoken conversation flow (its reply is read
   * aloud via TTS and must stay to one or two short sentences) — leave
   * it `false` (the default) for the typed health-chat box and the
   * dashboard's "Ask about this" button, both of which display the
   * reply as text and should get a real, complete answer. Regression
   * test for a real bug (2026-09-2x): every caller shared the voice-
   * tuned short/TTS-oriented prompt, so "Ask about this" got a one-line
   * non-answer with no explanation or lifestyle guidance.
   *
   * `skipMeasurementCheck` tells the edge node this message is
   * definitely a question about existing data, never a request to log
   * something new — pass `true` for "Ask about this" (its question is
   * always about an already-flagged value, by construction). Real bug
   * (2026-09-2x): the edge node's own punctuation-based guard against
   * misreading such a question as a record request didn't recognize
   * the full-width "？" the Chinese version of this question uses, so
   * the real model genuinely misclassified an LDL-cholesterol question
   * as a request to record blood glucose, replying with a nonsensical
   * "Got it, I've recorded your glucose as 3.6 mmol/l" instead of ever
   * answering the question. */
  async chat(
    userId: string,
    message: string,
    voice: boolean = false,
    skipMeasurementCheck: boolean = false,
  ): Promise<ChatResponse> {
    const response = await httpPostJson(this.bundle, `/users/${encodeURIComponent(userId)}/chat`, {
      message,
      voice,
      skip_measurement_check: skipMeasurementCheck,
    });
    return parseJsonOrThrow<ChatResponse>(response, 'chat');
  }

  /** Speech-to-text: `fileUri` is a local recording (e.g. from
   * `cordova-plugin-media`'s recorder). Stateless on the edge node —
   * doesn't require an unlocked session. */
  async transcribeAudio(userId: string, fileUri: string): Promise<TranscribeResponse> {
    const response = await httpUploadFile(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/voice/transcribe`,
      {},
      'file',
      fileUri,
    );
    return parseJsonOrThrow<TranscribeResponse>(response, 'transcribeAudio');
  }

  /** Same route as `transcribeAudio`, for a recording captured as
   * in-memory bytes (e.g. `voiceRecorder.ts`'s browser fallback, via
   * `MediaRecorder`, which has no native file:// path to hand
   * `transcribeAudio`) — same `httpUploadBytes` vs. `httpUploadFile`
   * split `uploadDocumentBytes` already uses for picked-file bytes. */
  async transcribeAudioBytes(userId: string, data: Uint8Array, mediaType: string): Promise<TranscribeResponse> {
    // Cosmetic only (the edge node decodes by the real bytes' own
    // container, not this filename — see ocr.py's OCR/faster-whisper
    // handling) — but should still name the file accurately. Covers
    // audio/mp4 (Safari's actual recording container, see
    // voiceRecorder.ts's mimeType selection) alongside the
    // Chromium/Firefox webm/ogg cases and native wav uploads.
    const extension = mediaType.includes('wav')
      ? 'wav'
      : mediaType.includes('mp4')
        ? 'mp4'
        : mediaType.includes('ogg')
          ? 'ogg'
          : 'webm';
    const response = await httpUploadBytes(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/voice/transcribe`,
      {},
      'file',
      `recording.${extension}`,
      mediaType,
      data,
    );
    return parseJsonOrThrow<TranscribeResponse>(response, 'transcribeAudioBytes');
  }

  /** Text-to-speech: returns real audio bytes for the caller to write to
   * a local file and play back (see `voiceRecorder.ts`) — this class
   * never plays audio itself. Also stateless. */
  async synthesizeSpeech(userId: string, text: string): Promise<SynthesizedAudio> {
    const response = await httpPostJsonForBinary(
      this.bundle,
      `/users/${encodeURIComponent(userId)}/voice/synthesize`,
      { text },
    );
    if (response.status === 503) {
      throw new DomiHealthApiError(response.status, 'synthesizeSpeech: no text-to-speech engine configured');
    }
    if (response.status < 200 || response.status >= 300 || response.data === null) {
      throw new DomiHealthApiError(response.status, `synthesizeSpeech failed (HTTP ${response.status})`);
    }
    return { data: response.data, contentType: response.contentType };
  }

  async deregisterDevice(userWallet: string, devicePubkey: string): Promise<{ transaction: string }> {
    const response = await httpPostJson(this.bundle, '/devices/deregister', {
      user_wallet: userWallet,
      device_pubkey: devicePubkey,
    });
    return parseJsonOrThrow<{ transaction: string }>(response, 'deregisterDevice');
  }
}
